import { deserify } from '@geneo2-web/shared-ui';
import { ChangeEvent, useEffect } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import {
  MAX_PROJECT_NAME_LENGTH,
  setProjectName,
  setSelectedClass,
  setSelectedSection,
  setSelectedSubject,
  resetProjectFlow,
  clearProjectSelection,
} from '../CreateProject/reducer/projectFlow.Slice';

import { getSectionsForClass } from '../../../utils/functions';
import {
  getProjectClassSections,
  getProjectSubjectsForClass,
} from '../projectSubjects.utils';
import { ProjectSelectionPopup } from './ProjectCreationPopup';
import { useNavigate } from 'react-router-dom';
import { Create_Project } from 'apps/teacher/src/routeHandling/RoutesNomenclature';

interface IProps {
  open: boolean;
  handleClose: () => void;
}

export const ProjectSubjectSelectionPopup = (props: IProps) => {
  const { open, handleClose } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const { selected_class, selected_section, selected_subject, project_name } =
    deserify(useAppSelector((state) => state.projectFlow));

  const getClassNamesAndClassIds = () => {
    const uniqueClassesMap = new Map<number, { name: string; id: number }>();

    //~ Only classes with a subject projects can be created for
    getProjectClassSections(user_info).forEach((item) => {
      if (!uniqueClassesMap.has(item.classId)) {
        uniqueClassesMap.set(item.classId, {
          id: item.classId,
          name: `Class ${item.className}`,
        });
      }
    });

    return Array.from(uniqueClassesMap.values()).sort(
      (a, b) => Number(a.name.split(' ')[1]) - Number(b.name.split(' ')[1])
    );
  };
  // Derive this from the current roster. deserify creates a new array each
  // render, so using that array in a state-setting effect causes a loop.
  const classList = getClassNamesAndClassIds();
  const sectionList = getSectionsForClass({
    classId: selected_class?.id,
    userInfo: user_info,
  }).filter(
    (section) =>
      getProjectSubjectsForClass({
        classId: selected_class?.id,
        sectionId: section.id,
        userInfo: user_info,
      }).length > 0
  );
  const subjectList = getProjectSubjectsForClass({
    classId: selected_class?.id,
    sectionId: selected_section?.id,
    userInfo: user_info,
  });

  const handleClassChange = (value: string) => {
    const classData = classList.find((item) => item.name === value);

    dispatch(setSelectedClass(classData));
    dispatch(setSelectedSection(undefined));
    dispatch(setSelectedSubject(undefined));
  };

  const handleSectionChange = (value: string) => {
    const sectionData = sectionList.find((item) => item.name === value);

    dispatch(setSelectedSection(sectionData));
    dispatch(setSelectedSubject(undefined));
  };

  const handleSubjectChange = (value: string) => {
    const subjectData = subjectList.find((item) => item.name === value);

    dispatch(setSelectedSubject(subjectData));
  };

  const handleProjectNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setProjectName(event.target.value));
  };

  const isProceedButtonDisabled = () => {
    return (
      !selected_class ||
      !selected_section ||
      !selected_subject ||
      !project_name?.trim() ||
      // A name restored from an older session can predate the input limit.
      project_name.trim().length > MAX_PROJECT_NAME_LENGTH
    );
  };

  const popupCloseHandler = () => {
    dispatch(resetProjectFlow());
    handleClose();
  };

  const handleProceed = async () => {
    // Keep the class/section/subject selection - the creation screen needs the
    // section id to scope which lesson plans the teacher can pick from.
    dispatch(clearProjectSelection());
    handleClose();
    navigate(`${Create_Project}/${selected_subject?.id}`);
  };
  useEffect(() => {
    if (open && class_subject_info) {
      const dashboardSectionSubjects = getProjectSubjectsForClass({
        classId: class_subject_info.classId,
        sectionId: class_subject_info.sectionId,
        userInfo: user_info,
      });
      //~ The dashboard's class and section are only carried over when projects
      //~ can be created there - otherwise they would not be in the dropdowns.
      if (dashboardSectionSubjects.length === 0) return;

      dispatch(
        setSelectedClass({
          id: class_subject_info.classId,
          name: `Class ${class_subject_info.classname}`,
        })
      );

      dispatch(
        setSelectedSection({
          id: class_subject_info.sectionId,
          name: class_subject_info.section,
        })
      );

      // The dashboard's subject is only carried over when projects are allowed
      // for it - otherwise the teacher picks from the narrowed dropdown.
      const preSelectableSubject = dashboardSectionSubjects.find(
        (subject) => subject.id === class_subject_info.subjectId
      );

      dispatch(setSelectedSubject(preSelectableSubject));
    }
  }, [open, class_subject_info?.classId]);
  return (
    <ProjectSelectionPopup
      popupOpen={open}
      popupCloseHandler={popupCloseHandler}
      popupTitle="Create Your Project"
      chooseClassData={{
        selectedClass: selected_class?.name ?? '',
        classList,
        handleClassChange,
      }}
      chooseSectionData={{
        selectedSection: selected_section?.name ?? '',
        sectionList,
        handleSectionChange,
        disabled: !selected_class,
      }}
      chooseSubjectData={{
        selectedSubject: selected_subject?.name ?? '',
        subjectList,
        handleSubjectChange,
        //~ Subjects are per section, so the section has to be picked first
        disabled: !selected_section,
      }}
      nameChangeData={{
        title: 'Project Name',
        name: 'projectName',
        value: project_name ?? '',
        onChange: handleProjectNameChange,
        maxLength: MAX_PROJECT_NAME_LENGTH,
      }}
      proceedButtonData={{
        disabled: isProceedButtonDisabled(),
        onClick: handleProceed,
      }}
    />
  );
};
